import React, { useEffect } from 'react';
import { Redirect, useHistory } from 'react-router';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@cpg-its/ads.common.button';
import Banner from '@cpg-its/ads.containers.banner';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { QuerySuccessMessage } from '../../../../../global/components/messages/query-success-message';
import { useQuery } from '../../../../../global/hooks/useQuery';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { FormTypes } from '../../../../account-info/models/form-models';
import { useNewHireSettings } from '../hooks/useNewHireSettings';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { Employee } from '../../employment/employment-model';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../constants/routes';
import { FormButtons } from '../../../../../global/components/form-element/form-buttons';
import { NewHireEnrollmentRoutes } from '../../new-hire-enrollment/models/new-hire-routes';
import { setIsReview, setNewHireStep } from '../new-hire-slice';
import { NewHireStep } from '../models/new-hire-model';
import { useCompensationState } from '../../new-hire-enrollment/hooks/useCompensationState';
import { isDioAdmin, isInstitutionOfficer } from '../../../../../global/helpers/auth-helpers';
import { useNewHireEnrollmentAllowed } from '../../new-hire-enrollment/hooks/useNewhireEnrollmentAllowed';
import { useEmploymentInfo } from '../../employment/useEmployment';

interface Props {
    settingsId: string;
}

export function NewHireConfirmation(props: Props) {
    const history = useHistory();
    const dispatch = useDispatch();
    const { settingsId } = props;

    const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
    const newHireProps = epiProps?.newHireSettingsState?.epiContent;
    const settingsLoaded = useNewHireSettings(settingsId);
    const formType = useQuery()?.get('success');
    const isStip = formType === FormTypes.STIP_NEW_HIRE;
    const currentEmployee = useSelector<TcsmStates, Employee>(state => state.employmentState.currentEmployee);

    const compensationLoaded = useCompensationState();
    const employmentLoaded = useEmploymentInfo();
    const gpsItem = useGpsType(isStip ? GpsType.NEW_HIRE_STIP_CONFIRMATION : GpsType.NEW_HIRE_NON_STIP_CONFIRMATION);

    const mapHome = useBasePath(ArcRoutes.ArcRoot);
    const reviewUrl = `${mapHome}${ArcRoutes.NewHireEnrollment}${NewHireEnrollmentRoutes.EmployeeRecord}${isStip ? ArcRoutes.StipNewHire : ArcRoutes.NonStipNewHire}`;
    const enrollUrl = `${mapHome}${ArcRoutes.NewHireEnrollment}${NewHireEnrollmentRoutes.WhoWillEnroll}`;
    const roleAllowed = isDioAdmin() || isInstitutionOfficer();
    const newHireEnrollmentAllowed = useNewHireEnrollmentAllowed();

    useEffect(() => {
        dispatch(setNewHireStep(NewHireStep.EmploymentCompensationClergy));
        dispatch(setIsReview(true));
    }, []);

    if (!settingsLoaded || (!compensationLoaded && isStip) || !employmentLoaded) {
        return <LoadingCircle />;
    }

    const name = currentEmployee?.first_name && currentEmployee?.last_name ?
        `${currentEmployee?.first_name} ${currentEmployee.last_name}`
        : currentEmployee?.full_name;

    function shouldShowWhoWillEnroll() {

        return isStip && newHireEnrollmentAllowed && roleAllowed;
    }

    const getContinueToWhoWillEnroll = () => {
        history.push(enrollUrl);
    };

    const getBackToReviewEmployeeRecord = () => {
        history.push(reviewUrl);
    };

    const getBackToMapHome = () => {
        history.push(mapHome);
    };


    if (!currentEmployee) {
        return <Redirect to={mapHome} />;
    }

    function RenderButtons() {
        if (shouldShowWhoWillEnroll()) {
            return (
                <FormButtons
                    disabled={false}
                    submitText={newHireProps?.confirmationContinueToWhoWillEnrollButtonText}
                    cancelText={newHireProps?.confirmationBackToReviewEmployeeRecordButtonText}
                    helperText={newHireProps?.confirmationNoteMessage}
                    onSubmit={getContinueToWhoWillEnroll}
                    onCancel={getBackToReviewEmployeeRecord}
                />
            );
        }

        return (
            newHireProps?.confirmationMAPHomeButtonWhenUnableToProceedToEnrollment &&
            <ButtonWrapper flexEnd>
                <>
                    <Button type='button' buttonStyle='tertiary' text={newHireProps?.confirmationMAPHomeButtonWhenUnableToProceedToEnrollment} backgroundColor='#808080' onClick={getBackToMapHome} />
                </>
            </ButtonWrapper>
        );
    }

    function GetNextStepTitleText() {
        if (isStip) {
            return shouldShowWhoWillEnroll() ? newHireProps.stipNextStepTitle : newHireProps.stipNextStepTitleWhenUnableToProceedToEnrollment;
        }

        return newHireProps.nonStipNextStepTitle;
    }

    function GetDescriptionText() {
        if (isStip) {
            return shouldShowWhoWillEnroll() ? newHireProps.stipConfirmationDescription : newHireProps.stipConfirmationDescriptionWhenUnableToProceedToEnrollment;
        }

        return newHireProps.nonStipConfirmationDescription;
    }


    if (formType === FormTypes.NEW_CLERGY_ENROLLED) {
        return (
            <PageContainer>
                <MainContent>
                    <ContentSpacer>
                        <Banner banner={{
                            type: 'warning',
                            label: newHireProps?.newClergyWarningTitle,
                            description: <WysiwygWithReactLink content={newHireProps?.newClergyWarningDescription} />,
                        }} />
                    </ContentSpacer>
                    <ContentSpacer>
                        <h1>{newHireProps?.newClergyConfirmationTitle}</h1>
                    </ContentSpacer>
                    <ContentSpacer>
                        <WysiwygWithReactLink content={newHireProps?.newClergyConfirmationPageDescription} />
                    </ContentSpacer>
                    <ButtonWrapper flexEnd>
                        <>
                            <Button type='button' buttonStyle='tertiary' text={newHireProps?.mapHomeButtonText} backgroundColor='#808080' onClick={getBackToMapHome} />
                        </>
                    </ButtonWrapper>
                </MainContent>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </PageContainer >
        );

    }

    return (
        <PageContainer>
            <MainContent>
                <ContentSpacer>
                    <QuerySuccessMessage successfulAction={isStip ? FormTypes.STIP_NEW_HIRE : FormTypes.NON_STIP_NEW_HIRE} />
                    <ContentSpacer>
                        <h1>{isStip ? newHireProps.stipConfirmationTitle : newHireProps.nonStipConfirmationTitle}</h1>
                    </ContentSpacer>
                    <ContentSpacer>
                        <h4>{name}</h4>
                        <p>{`${newHireProps?.clientNumberLabel}: ${currentEmployee?.masked_client_number}`}</p>
                    </ContentSpacer>
                    <h2>{GetNextStepTitleText()}</h2>

                </ContentSpacer>
                <ContentSpacer>
                    <WysiwygWithReactLink content={GetDescriptionText()} />
                </ContentSpacer>
                {RenderButtons()}
            </MainContent>
            <SideBarContent>
                <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
            </SideBarContent>
        </PageContainer>

    );
}